/**
 * Dalbe Reviews — a fully custom review carousel skinned to the brand.
 *
 * Captured-response-primary, DOM-fallback: Judge.me's own "Cards Carousel"
 * app block on this page (hidden via CSS, kept in the DOM — see
 * dalbe-reviews.liquid) is configured with max_reviews: 20, so scraping it
 * can never surface more than 20 of the real review count, and its DOM
 * doesn't expose replies or extra photos at all.
 *
 * This store's Judge.me runs as a Shopify Theme App Extension — confirmed
 * live that window.jdgm has no SHOP_TOKEN, and it fetches a
 * "reviews_for_carousel" endpoint rather than the classic
 * judge.me/api/v1/reviews — so there's no documented endpoint/token to call
 * directly. Instead, layout/theme.liquid installs an XHR/fetch interceptor as
 * early as possible in <head> (before Judge.me's own script can run) that
 * captures its real response into window.__dalbeJudgeMeReviews. This file
 * waits briefly for that to appear and uses it as the primary source (all
 * reviews, with replies/photos/avatars, no cap). DOM scraping only kicks in
 * if that capture never arrives, as a degraded-but-functional fallback (base
 * text only, capped at 20, no photos/replies) rather than an empty section.
 *
 * Field names in the captured response are still a guess (multiple fallback
 * chains below) since Judge.me doesn't document this extension's API shape —
 * if photos/replies still don't show after pushing, open devtools on the
 * live page: check Console for a "[dalbe-reviews]" warning, and check
 * Network → the "reviews_for_carousel" request's Response tab to see the
 * actual field names, then adjust normalizeApiReview() below to match.
 */

const i18nEl = document.getElementById('dalbe-reviews-i18n');
const i18n = i18nEl
  ? {
      shopDomain: i18nEl.dataset.shopDomain,
      replyLabel: i18nEl.dataset.replyLabel,
      readMore: i18nEl.dataset.readMore,
      emptyState: i18nEl.dataset.emptyState,
    }
  : {};

const track = document.getElementById('dalbe-reviews-track');
const loadingEl = document.getElementById('dalbe-reviews-loading');
const summaryEl = document.getElementById('dalbe-reviews-summary');
const summaryStarsEl = document.getElementById('dalbe-reviews-summary-stars');
const summaryAvgEl = document.getElementById('dalbe-reviews-summary-avg');
const summaryCountEl = document.getElementById('dalbe-reviews-summary-count');

if (track) {
  init();
}

async function init() {
  let reviews = await fetchViaApi();

  if (!reviews) {
    console.warn('[dalbe-reviews] API path unavailable (no token, or fetch failed) — falling back to DOM scrape of the hidden Judge.me carousel (capped at whatever it renders, no replies/photos).');
    reviews = scrapeCarousel();
  }

  if (!reviews.length) {
    if (loadingEl) loadingEl.textContent = i18n.emptyState || 'Hamarosan itt lesznek az első véleményeink.';
    return;
  }

  const cards = dedupeReviews(reviews);

  if (loadingEl) loadingEl.remove();
  buildSummary(cards);
  cards.forEach((card) => track.appendChild(buildCard(card)));
  wireCarouselNav();
  wireExpandedModal();
  wireLightbox();
}

/* ---------- Dedup ----------
 * Judge.me's own data has occasional exact-duplicate reviews imported under
 * different author records (e.g. a real name vs. a generic "PassportXXXXX"
 * placeholder) — same body, different metadata. Group by body text and keep
 * whichever copy has the most content (a reply, more photos). Short bodies
 * are left alone: a two-word review like "Szép hely!" can coincidentally
 * match between genuinely different reviewers, so only texts long enough to
 * be effectively unique are deduped. */

function dedupeReviews(reviews) {
  const bestByBody = new Map();
  const unique = [];

  reviews.forEach((review) => {
    const key = (review.body || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (key.length < 40) {
      unique.push(review);
      return;
    }
    const existing = bestByBody.get(key);
    if (!existing || reviewContentScore(review) > reviewContentScore(existing)) {
      bestByBody.set(key, review);
    }
  });

  return unique.concat(Array.from(bestByBody.values()));
}

function reviewContentScore(review) {
  let score = review.reply ? 1000 : 0;
  score += (review.photos ? review.photos.length : 0) * 100;
  score += review.body ? review.body.length : 0;
  return score;
}

/* ---------- API (primary source — not capped, includes replies/photos) ----------
 * This store's Judge.me runs as a Shopify Theme App Extension: no SHOP_TOKEN is
 * exposed and it doesn't call the classic judge.me/api/v1/reviews endpoint
 * (confirmed live — it calls "reviews_for_carousel" against a different host),
 * so there's no documented endpoint to call directly. Instead, an interceptor
 * installed as early as possible in layout/theme.liquid's <head> (before
 * Judge.me's own script can run) captures the real response from that request
 * into window.__dalbeJudgeMeReviews. This just waits for it to show up. */

async function fetchViaApi() {
  try {
    const rawReviews = await Promise.race([waitForCapturedReviews(), timeout(4500)]);
    if (!rawReviews) return null;
    return rawReviews.map(normalizeApiReview).filter((r) => r.author && r.body);
  } catch (err) {
    console.warn('[dalbe-reviews] Reading captured Judge.me response threw:', err);
    return null;
  }
}

function waitForCapturedReviews() {
  return new Promise((resolve) => {
    let tries = 0;
    (function check() {
      if (window.__dalbeJudgeMeReviews) resolve(window.__dalbeJudgeMeReviews);
      else if (++tries >= 40) resolve(null);
      else setTimeout(check, 100);
    })();
  });
}

function normalizeApiReview(r) {
  const reply =
    stripHtml(r.reply_content) ||
    r.reply ||
    r.merchant_reply ||
    r.reply_body ||
    r.shop_reply ||
    stripHtml(r.review_reply && (r.review_reply.body || r.review_reply.content)) ||
    (r.replies && r.replies[0] && (r.replies[0].body || r.replies[0].content)) ||
    '';

  const picList = r.pictures_urls || r.pictures || r.images || r.media || [];
  const photos = picList
    .map((p) => {
      const thumb = p.small || (p.urls && p.urls.small) || p.url || p.src || '';
      const full = p.original || (p.urls && p.urls.original) || thumb || '';
      const best = thumb || full;
      return best ? { thumb: best, full: full || best } : null;
    })
    .filter(Boolean)
    .slice(0, 4);

  const avatarSrc = (r.reviewer && r.reviewer.avatar_url) || r.reviewer_avatar_url || null;
  const author = ((r.reviewer && r.reviewer.name) || r.reviewer_name || r.author_name || '').trim();
  const body = (r.body || r.content || r.message || '').trim();
  const ratingRaw = r.rating || r.score || r.stars || 5;
  const rating = Math.max(1, Math.min(5, Math.round(Number(String(ratingRaw).split('/')[0]) || 5)));

  return {
    author,
    body,
    rating,
    avatarSrc,
    photos,
    reply: typeof reply === 'string' ? reply : '',
  };
}

function timeout(ms) {
  return new Promise((resolve) => setTimeout(() => resolve(null), ms));
}

function stripHtml(html) {
  return html ? String(html).replace(/<[^>]*>/g, '').trim() : '';
}

/* ---------- DOM scraping (fallback only — SSR carousel, capped, no reply/photos) ---------- */

function scrapeCarousel() {
  const itemSelector = '.jdgm-carousel-item, .jdgm-carousel__item, .jdgm-rev, [class*="jdgm-carousel-item"], [class*="jdgm-cards-carousel"] [class*="review"]';
  const nodes = Array.from(document.querySelectorAll(itemSelector));

  return nodes
    .map((node) => {
      const author = textOf(node, '.jdgm-reviewer-name, .jdgm-rev__author, [class*="reviewer-name"], [class*="rev__author"]');
      const body = textOf(node, '.jdgm-text, .jdgm-rev__body-text, [class*="review-text"], [class*="rev__body"]');
      if (!author || !body) return null;

      const starEls = node.querySelectorAll('.jdgm-star.jdgm--on, [class*="star"][class*="on"], [class*="star--filled"]');
      const rating = Math.min(starEls.length || 5, 5);

      const avatarImg = node.querySelector('img.jdgm-rev__author-pic, img[class*="author-pic"], img[class*="reviewer-pic"], .jdgm-rev__header img, [class*="avatar"] img');
      const avatarSrc = avatarImg ? lazySrc(avatarImg) : null;

      return { author, body, rating, reply: '', avatarSrc, photos: [] };
    })
    .filter(Boolean);
}

function textOf(node, selector) {
  const el = node.querySelector(selector);
  return el ? el.textContent.trim() : '';
}

function lazySrc(img) {
  const src = img.dataset.src || img.getAttribute('src') || '';
  if (!src || /no[-_]?photo|default\.|blank\.|placeholder/i.test(src)) return null;
  return src;
}

/* ---------- Card building (textContent only — reviews are user text) ---------- */

function buildTopRow(review, avatarClass, fallbackClass) {
  const top = document.createElement('div');
  top.className = 'dalbe-reviews__card-top';

  if (review.avatarSrc) {
    const img = document.createElement('img');
    img.className = avatarClass;
    img.src = review.avatarSrc;
    img.alt = '';
    img.onerror = () => img.replaceWith(buildAvatarFallback(review.author, fallbackClass));
    top.appendChild(img);
  } else {
    top.appendChild(buildAvatarFallback(review.author, fallbackClass));
  }

  const identity = document.createElement('div');
  identity.className = 'dalbe-reviews__identity';

  const nameEl = document.createElement('span');
  nameEl.className = 'dalbe-reviews__name';
  nameEl.textContent = review.author;
  identity.appendChild(nameEl);

  const starsEl = document.createElement('span');
  starsEl.className = 'dalbe-reviews__stars';
  starsEl.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
  identity.appendChild(starsEl);

  top.appendChild(identity);
  return top;
}

function buildAvatarFallback(author, className) {
  const el = document.createElement('div');
  el.className = className || 'dalbe-reviews__avatar-fallback';
  el.textContent = (author || '?').trim().charAt(0).toUpperCase();
  return el;
}

function buildCard(review) {
  const card = document.createElement('div');
  card.className = 'dalbe-reviews__card';
  // Triggers below (read more / photo count / reply hint) all just open the
  // same expanded view for THIS review — stashing the full object directly
  // on the card avoids re-serializing everything into dataset attributes.
  card._review = review;

  card.appendChild(buildTopRow(review, 'dalbe-reviews__avatar', 'dalbe-reviews__avatar-fallback'));

  const body = document.createElement('p');
  body.className = 'dalbe-reviews__body';
  body.textContent = review.body;
  card.appendChild(body);

  // "Read more" is the ONLY trigger that opens the expanded view — photo
  // thumbnails are static previews and the reply hint is a plain note, so
  // the button must show whenever there's anything beyond the card that the
  // user can't already see: truncated body text, photos, or a reply.
  // Everything is decided together (even the overflow-independent parts) so
  // the order stays fixed: read more, then photos, then reply hint at the
  // very bottom — regardless of the overflow check's own async timing.
  requestAnimationFrame(() => {
    const needsReadMore = body.scrollHeight > body.clientHeight + 2;
    const hasPhotos = !!(review.photos && review.photos.length);
    const hasReply = !!review.reply;

    if (needsReadMore || hasPhotos || hasReply) {
      card.appendChild(buildTrigger(i18n.readMore || 'Teljes vélemény', 'dalbe-reviews__toggle'));
    }
    if (hasPhotos) {
      card.appendChild(buildPhotoThumbs(review.photos));
    }
    if (hasReply) {
      const replyHint = document.createElement('p');
      replyHint.className = 'dalbe-reviews__reply-hint';
      replyHint.textContent = '* A tulajdonos válaszolt erre a véleményre';
      card.appendChild(replyHint);
    }
  });

  return card;
}

function buildTrigger(label, className) {
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = className;
  trigger.textContent = label;
  return trigger;
}

/* Small thumbnail row on the card itself (capped at 4, "+N" on the last one
   if there are more) — plain previews, not clickable; the full gallery only
   opens via the "read more" button. */
function buildPhotoThumbs(photos) {
  const wrap = document.createElement('div');
  wrap.className = 'dalbe-reviews__photos';
  const visible = photos.slice(0, 4);
  const hiddenCount = photos.length - visible.length;

  visible.forEach((photo, index) => {
    const thumb = document.createElement('div');
    thumb.className = 'dalbe-reviews__photo';

    const img = document.createElement('img');
    img.src = photo.thumb;
    img.alt = '';
    img.loading = 'lazy';
    img.onerror = () => thumb.remove();
    thumb.appendChild(img);

    if (hiddenCount > 0 && index === visible.length - 1) {
      const more = document.createElement('span');
      more.className = 'dalbe-reviews__photo-more';
      more.textContent = `+${hiddenCount}`;
      thumb.appendChild(more);
    }

    wrap.appendChild(thumb);
  });

  return wrap;
}

/* ---------- Summary (computed from the actual rendered set) ---------- */

function buildSummary(cards) {
  if (!cards.length || !summaryEl) return;
  const avg = cards.reduce((sum, c) => sum + c.rating, 0) / cards.length;
  const rounded = Math.round(avg);

  if (summaryStarsEl) summaryStarsEl.textContent = '★'.repeat(rounded) + '☆'.repeat(5 - rounded);
  if (summaryAvgEl) summaryAvgEl.textContent = avg.toFixed(2);
  if (summaryCountEl) summaryCountEl.textContent = `${cards.length} vélemény`;
  summaryEl.style.display = 'flex';
}

/* ---------- Carousel nav ----------
 * Clones the full card set on each side of the real one, so scrolling never
 * visibly hits an edge — once the user drifts into a cloned copy (via the
 * arrow buttons, a drag, or a trackpad swipe) a scroll listener silently
 * shifts scrollLeft by exactly one set's width to land back on the
 * equivalent real card. Since the clone is pixel-identical to the real set,
 * that shift is imperceptible and it reads as an infinite loop either way.
 *
 * Cloning runs inside its own requestAnimationFrame, scheduled (via the
 * forEach loop in init() that built each card, then this function) to fire
 * AFTER every card's own rAF that appends its read-more button / photo
 * thumbnails / reply hint — so the clones pick up that content too. cloneNode
 * doesn't copy expando JS properties, so card._review is re-attached by hand
 * (the expanded-view modal reads it off whichever card element was clicked,
 * clone or original). */

function wireCarouselNav() {
  const prevBtn = document.querySelector('[data-reviews-prev]');
  const nextBtn = document.querySelector('[data-reviews-next]');
  const cards = Array.from(track.querySelectorAll('.dalbe-reviews__card'));
  if (!cards.length) return;

  const step = cards[0].getBoundingClientRect().width + 20;

  if (prevBtn) prevBtn.addEventListener('click', () => track.scrollBy({ left: -step, behavior: 'smooth' }));
  if (nextBtn) nextBtn.addEventListener('click', () => track.scrollBy({ left: step, behavior: 'smooth' }));

  requestAnimationFrame(() => {
    const cloneBefore = cards.map((card) => {
      const clone = card.cloneNode(true);
      clone._review = card._review;
      return clone;
    });
    const cloneAfter = cards.map((card) => {
      const clone = card.cloneNode(true);
      clone._review = card._review;
      return clone;
    });
    cloneBefore.forEach((clone) => track.insertBefore(clone, track.firstChild));
    cloneAfter.forEach((clone) => track.appendChild(clone));

    const setWidth = cards[0].offsetLeft - cloneBefore[0].offsetLeft;
    track.scrollLeft = cards[0].offsetLeft;
    if (!setWidth) return;

    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const maxScroll = track.scrollWidth - track.clientWidth;
        if (track.scrollLeft < track.clientWidth) {
          track.scrollLeft += setWidth;
        } else if (track.scrollLeft > maxScroll - track.clientWidth) {
          track.scrollLeft -= setWidth;
        }
      });
    });
  });
}

/* ---------- Body scroll lock ----------
 * showModal() doesn't reliably stop the page behind it from scrolling on
 * mobile — confirmed live: touch-scrolling still moved the page while a
 * review was open. overflow:hidden on body alone is known not to hold on
 * iOS Safari; pinning body with position:fixed (and restoring scrollTop on
 * unlock) is the standard workaround. Counted rather than boolean because
 * the lightbox opens ON TOP of the already-open expanded view — closing
 * just the lightbox shouldn't unlock scroll while the expanded view is
 * still up, so it only actually unlocks once every open dialog has closed. */

let scrollLockDepth = 0;
let lockedScrollY = 0;

function lockBodyScroll() {
  if (scrollLockDepth === 0) {
    lockedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }
  scrollLockDepth++;
}

function unlockBodyScroll() {
  scrollLockDepth = Math.max(0, scrollLockDepth - 1);
  if (scrollLockDepth === 0) {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    // The theme sets html { scroll-behavior: smooth } globally, which a
    // plain scrollTo(x, y) call inherits — confirmed live, it turned this
    // restore into a visible animated scroll from the top instead of an
    // instant snap back. behavior: 'instant' bypasses that.
    window.scrollTo({ top: lockedScrollY, left: 0, behavior: 'instant' });
  }
}

/* ---------- Expanded view: full card (left) + photo gallery (right) ----------
 * Opened by any of the card's three triggers (read more / photo count /
 * reply hint) — whichever was clicked, the full review it belongs to is
 * read straight off card._review, so all three open the identical view. */

function wireExpandedModal() {
  const modal = document.querySelector('[data-reviews-expanded]');
  if (!modal) return;

  const topSlot = modal.querySelector('[data-reviews-expanded-top]');
  const bodyEl = modal.querySelector('[data-reviews-expanded-body]');
  const replyBlockEl = modal.querySelector('[data-reviews-expanded-reply]');
  const replyLabelEl = modal.querySelector('[data-reviews-expanded-reply-label]');
  const replyTextEl = modal.querySelector('[data-reviews-expanded-reply-text]');
  const galleryEl = modal.querySelector('[data-reviews-expanded-gallery]');

  track.addEventListener('click', (event) => {
    const trigger = event.target.closest('.dalbe-reviews__toggle');
    if (!trigger) return;
    const review = trigger.closest('.dalbe-reviews__card')._review;
    if (!review) return;

    topSlot.replaceChildren(buildTopRow(review, 'dalbe-reviews-expanded__avatar', 'dalbe-reviews-expanded__avatar-fallback'));
    bodyEl.textContent = review.body;

    if (review.reply) {
      replyLabelEl.textContent = i18n.replyLabel || 'Válasz a tulajdonostól';
      replyTextEl.textContent = review.reply;
      replyBlockEl.style.display = 'block';
    } else {
      replyBlockEl.style.display = 'none';
    }

    galleryEl.replaceChildren();
    if (review.photos && review.photos.length) {
      review.photos.forEach((photo, index) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dalbe-reviews-expanded__photo';
        btn.dataset.reviewsPhotoIndex = String(index);
        const img = document.createElement('img');
        img.src = photo.thumb;
        img.alt = '';
        img.onerror = () => btn.remove();
        btn.appendChild(img);
        galleryEl.appendChild(btn);
      });
      galleryEl._photos = review.photos;
      galleryEl.style.display = 'grid';
    } else {
      galleryEl.style.display = 'none';
    }

    lockBodyScroll();
    modal.showModal();
  });

  modal.querySelector('[data-reviews-expanded-close]').addEventListener('click', () => modal.close());
  modal.addEventListener('click', (event) => {
    if (event.target === modal) modal.close();
  });
  modal.addEventListener('close', unlockBodyScroll);
}

/* ---------- Lightbox — zooms a single photo from the expanded view's gallery ---------- */

function wireLightbox() {
  const lightbox = document.querySelector('[data-reviews-lightbox]');
  const gallery = document.querySelector('[data-reviews-expanded-gallery]');
  if (!lightbox || !gallery) return;

  const image = lightbox.querySelector('[data-reviews-lightbox-image]');
  const counter = lightbox.querySelector('[data-reviews-lightbox-counter]');
  let currentIndex = 0;

  function show(index) {
    const photos = gallery._photos || [];
    if (!photos.length) return;
    currentIndex = (index + photos.length) % photos.length;
    image.src = photos[currentIndex].full;
    image.alt = '';
    counter.textContent = `${currentIndex + 1} / ${photos.length}`;
  }

  gallery.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-reviews-photo-index]');
    if (!btn) return;
    show(Number(btn.dataset.reviewsPhotoIndex));
    lockBodyScroll();
    lightbox.showModal();
  });

  lightbox.querySelector('[data-reviews-lightbox-close]').addEventListener('click', () => lightbox.close());
  lightbox.querySelector('[data-reviews-lightbox-prev]').addEventListener('click', () => show(currentIndex - 1));
  lightbox.querySelector('[data-reviews-lightbox-next]').addEventListener('click', () => show(currentIndex + 1));

  lightbox.addEventListener('click', (event) => {
    if (event.target === lightbox) lightbox.close();
  });
  lightbox.addEventListener('close', unlockBodyScroll);

  lightbox.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') show(currentIndex + 1);
    if (event.key === 'ArrowLeft') show(currentIndex - 1);
  });
}
